// submission-findings.js — Layer 1A: deterministic provider-identity findings
// for the admin submission review card.
//
// ‼️ WHAT THIS LAYER IS, AND THE LINE IT MUST NOT CROSS.
//
//   The database or a rule can prove it   → a Layer 1 finding (this file)
//   The evidence document must be read    → a Layer 2 finding (does not exist yet)
//   Neither                               → nothing is written, silently or otherwise
//
// Everything here is a pure function of (payload, entity dictionary). No
// network, no clock, no model, no writes. A finding DESCRIBES; the admin acts.
// `action` below is a SUGGESTION the card renders as a button — this module
// never mutates a payload and nothing calls it with intent to save.
//
// ‼️ FACT AND INFERENCE ARE DIFFERENT FIELDS, ON PURPOSE. `observed` is what is
// true of the data. `recommendedAction` is what a human might do about it. A
// finding that collapses the two — "this submission is restricted" instead of
// "58/58 comparable rows are restricted; check the restriction fields" — is
// asserting a policy conclusion the data does not contain. The identity
// findings below are the ones where the two genuinely coincide, which is why
// this batch went first.
//
// ‼️ THE DICTIONARY IS window.PROVIDER_LABELS AND IT LIES ABOUT CHINESE.
// data.js builds it as `zh: r.label_zh || r.label_en` — so an entity with no
// Chinese name reports its ENGLISH name as `.zh`. Measured 2026-09-09: 2 of 105
// entities are in that state. Any finding that offers "the entity's Chinese
// name" must therefore test `zh !== en`, never `zh != null`. Handing an admin
// the English name labelled as the Chinese one is the same class of error as
// the `labelZh` fallback refused in identity-candidates (§114).

// ── Normalisation ────────────────────────────────────────────────────────────
// ‼️ WHITESPACE AND CASE ONLY. Not punctuation, not width, not fuzzing.
//
// A search box may match loosely — the human reads the result and decides. A
// FINDING asserts, so it must be strictly tighter than the picker's search
// predicate, not looser. `"American Heart Association."` therefore does NOT
// match the entity `American Heart Association`: the trailing period is a real
// difference in the submitted text, and it belongs to the completeness batch
// ("trailing punctuation on a name about to mint an entity"), which is a
// separate finding with a separate action. Silently absorbing it here would
// hide the very defect that batch exists to report.
function fNorm(value) {
  return String(value == null ? '' : value).trim().replace(/\s+/g, ' ').toLowerCase();
}

// Han characters. Deliberately not "any non-ASCII": an accented Latin provider
// name is not a Chinese name, and must not be reported as one.
const F_CJK = /[㐀-䶿一-鿿豈-﫿]/;
function fHasCJK(value) {
  return F_CJK.test(String(value == null ? '' : value));
}

// An entity's Chinese name, or null when it does not really have one.
// See the dictionary warning above — this is the ONLY way to ask.
function fEntityZh(entry) {
  const en = entry && entry.en ? String(entry.en).trim() : '';
  const zh = entry && entry.zh ? String(entry.zh).trim() : '';
  if (!zh) return null;
  if (en && fNorm(zh) === fNorm(en)) return null; // a mirror, not a translation
  return zh;
}
function fEntityEn(entry) {
  const en = entry && entry.en ? String(entry.en).trim() : '';
  return en || null;
}

// The dictionary may be a Map (window.PROVIDER_LABELS) or a plain object, so
// tests and callers are not forced to build one shape.
function fEntries(dict) {
  if (!dict) return [];
  const out = [];
  if (typeof dict.forEach === 'function' && typeof dict.get === 'function') {
    dict.forEach((v, k) => out.push([String(k), v]));
    return out;
  }
  Object.keys(dict).forEach(k => out.push([String(k), dict[k]]));
  return out;
}

// ── PROVIDER_LABEL_MATCH ────────────────────────────────────────────────────
// ‼️ THREE VERDICTS, NEVER A BOOLEAN. `matched: true/false` cannot say that two
// entities answer to the same name, so the day an alias or an overlapping name
// appears, a boolean reports the first hit as though it were the only one and an
// admin attaches a course to the wrong organisation with a green tick next to
// it. `ambiguous_label_match` is a real answer and must stay reachable.
//
// ‼️ AND TODAY IT IS A LATENT BRANCH, WHICH IS THE POINT. Measured 2026-09-09:
// zero names are shared by two entities in the live dictionary, so this verdict
// never fires in production right now and no live sample can prove it works.
// scripts/test-submission-findings.mjs constructs the collision deliberately —
// a branch whose only test is "real data never hits it" is a branch that will
// be wrong the first time real data does.
//
// Both payload sides are considered, because the language slots are wrong often
// enough (12 of 12 measured) that trusting them would decide the answer from the
// error. Both entity names are considered for the same reason.
function providerLabelMatch(payload, dict) {
  const submitted = [payload && payload.provider_full_en, payload && payload.provider_full_zh]
    .map(v => (v == null ? '' : String(v).trim()))
    .filter(Boolean);
  if (!submitted.length) {
    return { code: 'PROVIDER_LABEL_MATCH', verdict: 'no_direct_label_match',
             severity: 'info', submitted: [], entities: [],
             observed: 'no_provider_text', action: null };
  }
  const keys = submitted.map(fNorm);
  const hits = [];
  fEntries(dict).forEach(([id, entry]) => {
    const en = fEntityEn(entry);
    const zh = fEntityZh(entry);
    // ‼️ Match against the entity's RAW zh, not fEntityZh: an entity whose
    // Chinese name mirrors its English one must still be matchable by that
    // text. fEntityZh answers "is there a translation to offer", which is a
    // different question from "does this name identify this entity".
    const rawZh = entry && entry.zh ? String(entry.zh).trim() : '';
    const names = [en, rawZh].filter(Boolean).map(fNorm);
    if (names.some(n => keys.includes(n))) hits.push({ entityId: id, en, zh });
  });
  if (hits.length === 1) {
    return { code: 'PROVIDER_LABEL_MATCH', verdict: 'exact_label_match',
             severity: 'ok', submitted, entities: hits,
             observed: 'matches_one_existing_label',
             // The only action in this batch that sets anything, and it sets
             // the DISPOSITION the admin was going to have to answer anyway —
             // not a payload field. Still a click.
             action: { kind: 'set_provider_disposition', entityId: hits[0].entityId } };
  }
  if (hits.length > 1) {
    return { code: 'PROVIDER_LABEL_MATCH', verdict: 'ambiguous_label_match',
             severity: 'warn', submitted, entities: hits,
             observed: 'matches_multiple_existing_labels',
             // ‼️ NO ACTION. Offering "attach" here would make the card pick one
             // of them, which is the exact failure the third verdict exists to
             // prevent. The admin opens the picker and decides.
             action: null };
  }
  // ‼️ `info`, NOT `warn`, AND THE MEASUREMENT IS THE REASON.
  //
  // The tempting reading of this verdict is "approving this will mint a new
  // organisation". Measured against production 2026-09-09, that reading is
  // wrong 41 times out of 43: of the approved submissions whose text matches no
  // entity name, 41 were resolved onto an EXISTING entity anyway, because the
  // alias/contract layer resolves names this dictionary does not carry. Only 2
  // ended up without a known entity.
  //
  // So the fact is "the submitted text is not equal to any known organisation's
  // name". That is all it is. It does not predict the resolver, it is not
  // evidence of novelty, and it fires on 57 of 82 live submissions — which is
  // exactly why it must not wear a warning colour. A badge that cries wolf on
  // two thirds of the queue trains an admin to stop reading badges.
  return { code: 'PROVIDER_LABEL_MATCH', verdict: 'no_direct_label_match',
           severity: 'info', submitted, entities: [],
           observed: 'matches_no_existing_label',
           // ‼️ AND THE FINDING SAYS WHAT IT DOES NOT KNOW. Carried as data so
           // the card cannot quietly render a stronger claim than was measured.
           doesNotImply: 'provider_is_new',
           // ‼️ Still no action. The name may be a variant of an entity sitting
           // right there (「養和醫療集團 (Hong Kong Sanatorium …)」 matches
           // nothing exactly and is obviously 養和). Minting is the resolver's
           // job under an explicit human verdict, and this finding must never
           // look like a shortcut to it.
           action: null };
}

// ── PROVIDER_LANGUAGE_FIELD_MISMATCH ─────────────────────────────────────────
// Pure text property of the payload; needs no dictionary and no entity.
// Measured 2026-09-09: 12 of 12 submissions carrying Chinese provider text kept
// it in provider_full_en.
function providerLanguageFieldMismatch(payload) {
  const en = payload && payload.provider_full_en ? String(payload.provider_full_en).trim() : '';
  const zh = payload && payload.provider_full_zh ? String(payload.provider_full_zh).trim() : '';
  const issues = [];
  if (en && fHasCJK(en)) issues.push({ field: 'provider_full_en', expected: 'en', found: 'cjk', value: en });
  // ‼️ ASYMMETRIC ON PURPOSE. Chinese-in-the-English-field is unambiguous. The
  // reverse is not: a Hong Kong organisation's Chinese-field value is very often
  // legitimately Latin (an acronym, "HKU SPACE", a bare English name used as the
  // official Chinese-language brand). Flagging "no CJK in provider_full_zh"
  // would fire constantly on correct data, and a finding that cries wolf is
  // worse than no finding. Only the provable direction is reported.
  if (!issues.length) return null;
  return { code: 'PROVIDER_LANGUAGE_FIELD_MISMATCH', severity: 'warn',
           issues, observed: 'cjk_text_in_english_field',
           // Swap is only offered when the destination is free; overwriting a
           // populated provider_full_zh is a data decision, not a tidy-up.
           action: (!zh && issues.length === 1)
             ? { kind: 'swap_provider_language_fields', from: 'provider_full_en', to: 'provider_full_zh' }
             : null };
}

// ── PROVIDER_BILINGUAL_GAP ───────────────────────────────────────────────────
// Only meaningful once identity is settled: without an entity there is no
// authority for what the other name IS, and guessing one is a translation this
// layer is not entitled to make.
function providerBilingualGap(payload, dict, matchFinding) {
  const m = matchFinding || providerLabelMatch(payload, dict);
  if (m.verdict !== 'exact_label_match') return null;
  const ent = m.entities[0];
  const en = fEntityEn(ent);
  const zh = fEntityZh(ent);          // ‼️ null when it only mirrors English
  if (!en || !zh) return null;        // the entity itself is not bilingual
  const pEn = payload && payload.provider_full_en ? String(payload.provider_full_en).trim() : '';
  const pZh = payload && payload.provider_full_zh ? String(payload.provider_full_zh).trim() : '';
  // ‼️ NEVER OFFER A VALUE THE PAYLOAD ALREADY HOLDS SOMEWHERE ELSE.
  //
  // Found by rendering the card, not by reading the code. A submission whose
  // provider_full_en contains 「基督教靈實協會」 raises BOTH the language-field
  // mismatch (Chinese in the English slot) and, naively, this gap — because
  // provider_full_zh is empty and the matched entity has a Chinese name. The two
  // findings then describe one problem twice, and their buttons disagree:
  //
  //   [對調] → en: null,          zh: 基督教靈實協會      ← correct
  //   [採用] → en: 基督教靈實協會, zh: 基督教靈實協會      ← the same string twice,
  //                                                        and en still wrong
  //
  // Whichever the reviewer pressed first would decide whether the row ended up
  // right. So the gap withdraws wherever the value is already present in the
  // payload: this is not a missing translation, it is a misfiled one, and the
  // mismatch finding owns it.
  const held = [pEn, pZh].filter(Boolean).map(fNorm);
  const missing = [];
  if (!pEn && !held.includes(fNorm(en))) missing.push({ field: 'provider_full_en', value: en });
  if (!pZh && !held.includes(fNorm(zh))) missing.push({ field: 'provider_full_zh', value: zh });
  if (!missing.length) return null;
  return { code: 'PROVIDER_BILINGUAL_GAP', severity: 'info',
           entityId: ent.entityId, missing,
           observed: 'entity_is_bilingual_payload_is_not',
           action: { kind: 'adopt_entity_names', entityId: ent.entityId, fields: missing } };
}

// ═════════════════════════════════════════════════════════════════════════════
// LAYER 1B — COMPLETENESS FINDINGS
//
// ‼️ THE INVARIANT THAT SEPARATES 1B FROM GUESSING: this layer may normalise
// presentation-safe noise, and may NEVER invent a domain value from a missing
// field. Stripping a trailing space off a provider name is deterministic and
// lossless. Turning `end_date: null` into `start_date` is a claim about what
// the course WAS, and nothing in the submission schema licenses it.
//
// ‼️ AND EVERY `observed` NAMES STORAGE STATE, NOT INTENT. Measured 2026-09-10:
// the co-submit door writes `Number(form.points) || null`, so a nurse who types
// 0 is stored as null — while the other two doors store 0. `cne_points === null`
// therefore proves what is in the column and nothing about what anyone filled
// in. Copy that says "提交者未填寫" would be asserting the one thing this layer
// cannot see. (The three-door disagreement is a real defect and NOT 1B's to
// fix; 1B describes the state that reached it.)
// ═════════════════════════════════════════════════════════════════════════════

// ── CNE_POINTS_MISSING / CNE_POINTS_ZERO ─────────────────────────────────────
// ‼️ TWO FINDINGS, NEVER ONE `INVALID_CNE_POINTS`. Nothing in the corpus or the
// contract establishes that 0 is illegal — a genuinely free, non-accredited
// session could be 0 — so merging them would have this layer ruling on a domain
// question it has no evidence for. Measured 2026-09-10: null 2/82, zero 0/82.
// Zero is LATENT but reachable: two of the three write doors store it.
function cnePointsFindings(payload) {
  const v = payload ? payload.cne_points : undefined;
  if (v === null || v === undefined) {
    return { code: 'CNE_POINTS_MISSING', severity: 'warn',
             observed: 'cne_points_is_null',
             // The consequence IS provable, unlike the cause.
             impact: 'no_points_available_to_count',
             action: null };
  }
  if (v === 0) {
    return { code: 'CNE_POINTS_ZERO', severity: 'info',
             observed: 'cne_points_is_zero', value: 0,
             action: null };
  }
  return null;
}

// ── END_DATE_MISSING ─────────────────────────────────────────────────────────
// ‼️ NEVER MIRROR start_date. 30 of 82 submissions DO carry start === end, and
// the AI extractor mirrors single-date documents, so "just copy it" looks
// obviously right. It is not licensed: no schema or product contract defines a
// missing end_date as a one-day course.
//
// And unlike the provider case, nothing downstream repairs this — measured
// 2026-09-10, 33 of 33 approved submissions with a null end_date produced a
// catalogue row that is STILL null. So this is a persistent gap worth naming,
// and still not a value this layer may invent.
//
// 39 of 82 submissions land here (~48%), which is why it is `info` with no
// action: at that frequency a warning would out-weigh the findings that need
// handling, and every-card-has-a-notice ends with nobody reading any of them.
function endDateMissing(payload) {
  const start = payload && payload.start_date ? String(payload.start_date).trim() : '';
  const end = payload && payload.end_date ? String(payload.end_date).trim() : '';
  if (!start || end) return null;
  return { code: 'END_DATE_MISSING', severity: 'info',
           observed: 'start_date_present_end_date_null',
           start_date: start, action: null };
}

// ── PROVIDER_NAME_TRAILING_NOISE ─────────────────────────────────────────────
// ‼️ "ENDS IN A FULL STOP" IS NOT "WRONG", AND THE DICTIONARY PROVES IT.
// `Abundant Wellness Co. Ltd.` is a real provider label that ends in a period.
// So the suppression rule is a dictionary FACT, not a punctuation heuristic: a
// value that already IS a known label is a name, whatever it ends with.
//
// ‼️ THE AS-IS COMPARISON MUST NOT TRIM. fNorm() trims, so `"Hospital Authority "`
// would "match as-is" under it and the whitespace finding would suppress itself
// — the one case that is unambiguously noise. Case-fold only here; trimming is
// exactly the difference being reported.
function fCaseOnly(value) {
  return String(value == null ? '' : value).toLowerCase();
}
const F_TERMINAL_PUNCT = /[.。,，;；:：、]+$/;

function providerNameTrailingNoise(payload, dict) {
  const labels = [];
  fEntries(dict).forEach(([, entry]) => {
    if (entry && entry.en) labels.push(entry.en);
    if (entry && entry.zh) labels.push(entry.zh);
  });
  const asIsKnown = new Set(labels.map(fCaseOnly));
  const normKnown = new Set(labels.map(fNorm));
  const issues = [];
  for (const field of ['provider_full_en', 'provider_full_zh']) {
    const raw = payload && payload[field] != null ? String(payload[field]) : '';
    if (!raw) continue;
    // ‼️ THE SUPPRESSION. A value that already is a known label is a name.
    if (asIsKnown.has(fCaseOnly(raw))) continue;
    if (raw !== raw.trim()) {
      const candidate = raw.trim();
      issues.push({ field, kind: 'trailing_whitespace', original: raw, candidate,
                    as_is_exact_label_match: false,
                    candidate_exact_label_match: normKnown.has(fNorm(candidate)) });
      continue;   // one issue per field: fix the whitespace, then look again
    }
    if (F_TERMINAL_PUNCT.test(raw)) {
      // ‼️ STRIP MINIMALLY, AND LET THE DICTIONARY DECIDE HOW FAR.
      //
      // A greedy strip cannot ever propose a candidate that itself ends in
      // punctuation, so `Abundant Wellness Co. Ltd..` would yield
      // `Abundant Wellness Co. Ltd` — dropping the period that is part of the
      // real name and quietly proposing a name that does not exist.
      //
      // So: peel one trailing character at a time and stop at the first
      // candidate that IS a known label; only if none is, fall back to peeling
      // them all. Still fully deterministic, and it uses the same authority as
      // the suppression rule above rather than a second opinion about
      // punctuation.
      let candidate = null;
      let peeled = raw.trim();
      while (F_TERMINAL_PUNCT.test(peeled)) {
        peeled = peeled.replace(/[.。,，;；:：、]$/, '').trim();
        if (!peeled) break;
        if (normKnown.has(fNorm(peeled))) { candidate = peeled; break; }
      }
      if (!candidate) candidate = raw.replace(F_TERMINAL_PUNCT, '').trim();
      if (!candidate) continue;
      issues.push({ field, kind: 'terminal_punctuation', original: raw, candidate,
                    as_is_exact_label_match: false,
                    candidate_exact_label_match: normKnown.has(fNorm(candidate)) });
    }
  }
  if (!issues.length) return null;
  // ‼️ ONLY WHITESPACE CARRIES AN ACTION, AND ONLY BECAUSE IT IS LOSSLESS.
  // Terminal punctuation gets an observation and nothing else: the candidate is
  // "the value without its trailing punctuation", never "the correct name".
  // The future branch — original is not a label AND the candidate matches
  // exactly one — is recorded in `candidate_exact_label_match` and has no UI,
  // because it has zero production cases (measured 2026-09-10: the single live
  // instance, `American Heart Association.`, matches nothing either way).
  const ws = issues.filter(i => i.kind === 'trailing_whitespace');
  return { code: 'PROVIDER_NAME_TRAILING_NOISE', severity: 'info', issues,
           observed: 'provider_name_has_trailing_noise',
           action: ws.length
             ? { kind: 'strip_trailing_whitespace',
                 fields: ws.map(i => ({ field: i.field, value: i.candidate })) }
             : null };
}

function completenessFindings(payload, dict) {
  const out = [];
  const pts = cnePointsFindings(payload); if (pts) out.push(pts);
  const noise = providerNameTrailingNoise(payload, dict); if (noise) out.push(noise);
  const ed = endDateMissing(payload); if (ed) out.push(ed);
  return out;
}

// ═════════════════════════════════════════════════════════════════════════════
// LAYER 1C — CONVENTION FINDINGS
//
// ‼️ ONE FINDING SHIPPED OUT OF THREE CANDIDATES, AND THAT IS THE RESULT, NOT A
// SHORTFALL. The calibration that preceded this (RELEASE.md, Layer 1C) rejected
// the restriction-precedent finding outright and deferred submitter affiliation.
// Read that section before proposing either again — in particular, the
// "58/58 of Hospital Authority's courses are restricted" figure that motivates
// the precedent idea is a COHORT ARTIFACT, not a provider precedent.
// ═════════════════════════════════════════════════════════════════════════════

// ── KNOWN_FACILITY_IN_PROVIDER_FIELD ─────────────────────────────────────────
// ‼️ THE NAME IS THE WHOLE CLAIM. What a dictionary lookup establishes is that
// the provider text names a known FACILITY. It does not establish that the
// provider is wrong, that the row belongs to the facility's parent, or which
// organisation should own it — NCHK accredits organisations and a facility is
// simply not one, but which organisation is a resolution question with a human
// in it. Hence not WRONG_PROVIDER_TYPE, and not FACILITY_NOT_ORGANISATION.
//
// ‼️ AND IT SUPPRESSES WHEN THE TEXT ALSO NAMES AN ACCREDITED ORGANISATION.
// Measured 2026-09-10: 6 of 82 live submissions name a known facility, and 3 of
// those 6 read 「養和醫療集團 (Hong Kong Sanatorium & Hospital Medical Group)」 —
// where 養和醫療集團 IS the accredited organisation and the facility name is
// helpful extra detail. Telling that submitter they named a facility would be
// scolding them for being MORE precise than required. The remaining 3 (PYNEH
// twice, a bare HKSH once) are the real cases.
//
// Dictionary is hk-hospitals.js — the same canonical list the workplace picker
// uses, so a facility added there is inspected here automatically. Every name in
// it is ≥12 characters, so substring matching carries no short-token risk; the
// length floor below is belt-and-braces for future additions.
// ‼️ THE FLOOR IS SCRIPT-AWARE, AND IT HAS TO BE. A single length threshold
// calibrated on Latin names silently rejects Chinese ones: 「養和醫療集團」 is
// SIX characters and is a fully specific organisation name, so an 8-character
// floor suppressed the suppression — the 養和 submissions kept firing as
// "facility named" even though they name the accredited organisation outright.
// A CJK name carries roughly twice the information per character; 4 there is as
// specific as 8 in Latin.
const F_MIN_LATIN = 8;
const F_MIN_CJK = 4;
function fMinLen(name) { return fHasCJK(name) ? F_MIN_CJK : F_MIN_LATIN; }

function knownFacilityInProviderField(payload, dict, facilities) {
  const src = facilities
    || (typeof window !== 'undefined' && window.HK_HOSPITALS) || null;
  if (!src) return null;
  const list = Array.isArray(src)
    ? src
    : [].concat(src.public || [], src.private || []);
  if (!list.length) return null;

  const text = ['provider_full_en', 'provider_full_zh']
    .map(f => (payload && payload[f] != null ? String(payload[f]) : ''))
    .filter(Boolean).join(' ').trim();
  if (!text) return null;
  const hay = fCaseOnly(text);
  const contains = (name) => !!name && String(name).length >= fMinLen(name)
    && hay.includes(fCaseOnly(name));

  const facility = list.find(f => f && (contains(f.en) || contains(f.zh)));
  if (!facility) return null;

  // ‼️ THE SUPPRESSION. If the text ALSO names an accredited organisation, the
  // submitter already answered the question this finding would ask.
  let namedOrg = null;
  fEntries(dict).forEach(([, entry]) => {
    if (namedOrg) return;
    if (entry && contains(entry.en)) namedOrg = entry.en;
    else if (entry && contains(entry.zh)) namedOrg = entry.zh;
  });
  if (namedOrg) return null;

  return { code: 'KNOWN_FACILITY_IN_PROVIDER_FIELD', severity: 'warn',
           observed: 'provider_text_matches_known_facility',
           submitted_text: text,
           facility: { key: facility.key || null, en: facility.en || null,
                       zh: facility.zh || null },
           dictionary: 'hk-hospitals.js',
           // ‼️ NO ACTION. A dictionary match is a review trigger. Which
           // organisation should own the row is a resolution question, and
           // rewriting provider identity from a facility hit would be this
           // layer deciding one.
           action: null };
}

function conventionFindings(payload, dict, facilities) {
  const out = [];
  const f = knownFacilityInProviderField(payload, dict, facilities);
  if (f) out.push(f);
  return out;
}

// ═════════════════════════════════════════════════════════════════════════════
// DUPLICATE REVIEW FINDINGS
//
// ‼️ THIS IS A REVIEW SIGNAL, NOT A GUARD. It never rejects, never blocks, never
// touches submission lifecycle. The existing duplicate guard owns blocking,
// 23505 and ALREADY_PENDING semantics, where a wrong answer is expensive; this
// only puts a pair in front of a human. Do not merge the two.
//
// ── Two stages, and only the FIRST decides who is a candidate ────────────────
//
//   Stage 1  submitter + start_date + cne_points   → the candidate set
//   Stage 2  explainable token overlap             → evidence and ranking ONLY
//
// ‼️ STAGE 2 MUST NEVER REMOVE A STAGE-1 CANDIDATE. Measured 2026-09-11 over 82
// live submissions: Stage 1 cuts 3321 possible pairs to 17, and within those 17
// a hand-labelled 12 are real duplicates with >= 5 shared title tokens while the
// other 5 share ZERO. That separation is clean — and it is calibration evidence
// from one hand-labelled corpus, not a law. It supports "shared tokens are a
// useful signal". It does NOT support "no shared tokens means not a duplicate",
// so absence of overlap weakens a candidate and never deletes it.
//
// ── Why both title AND provider tokens, when title alone scored 12/12 ────────
// The durable finding from that calibration was not either percentage. It was:
// provider text drifts (the same CLTI webinar was filed as "Pamela Youde
// Nethersole Eastern Hospital" and as "Department of Surgery …, Pamela Youde
// Nethersole Eastern Hospital") and title text drifts ("Lase"→"Last", a "2nd "
// prefix, case), but no true pair has yet lost BOTH. Two independent signals
// preserve that; one blended similarity score would destroy it and be
// unexplainable besides.
//
// ‼️ NO SIMILARITY SCORE. Set intersection only, and the tokens are carried out
// so the card can print them. A reviewer can check "chronic · limb ·
// threatening · ischemia" themselves; they cannot check 0.87.
//
// ‼️ KNOWN LIMIT, STATED RATHER THAN PAPERED OVER: tokens split on whitespace
// and punctuation, so a CJK title is ONE token and only matches exactly. No CJK
// segmentation is attempted — inventing one here would be a second unvalidated
// mechanism inside a layer whose whole point is explainability.
const F_TOKEN_MIN = 3;
function fTokens(value) {
  return new Set(
    String(value == null ? '' : value)
      .toLowerCase()
      .replace(/[^\p{L}\p{N}\s]+/gu, ' ')
      .split(/\s+/)
      .filter(t => t.length >= F_TOKEN_MIN));
}
function fShared(a, b) {
  const A = fTokens(a); const B = fTokens(b);
  return [...A].filter(t => B.has(t)).sort();
}

// A pair is keyed by the two ids in a STABLE order, so the same pair is the same
// pair whichever side is being reviewed.
function fPairKey(a, b) { return [String(a), String(b)].sort().join('|'); }

function duplicateReviewFindings(subject, corpus) {
  const P = (s) => (s && s.payload) || {};
  if (!subject || !Array.isArray(corpus)) return [];
  const sid = subject.id == null ? null : String(subject.id);
  const sp = P(subject);
  if (!subject.submitter_user_id || !sp.start_date) return [];
  const pairs = [];
  for (const other of corpus) {
    if (!other || String(other.id) === sid) continue;
    const op = P(other);
    // ── Stage 1 ──────────────────────────────────────────────────────────────
    // ‼️ cne_points is compared with ===, so null === null counts as agreement
    // and null vs 0 does not. Those are different facts (see §116 and issue
    // #42: one door stores a typed 0 as null), and collapsing them here would
    // import that defect into a second place.
    if (other.submitter_user_id !== subject.submitter_user_id) continue;
    if (op.start_date !== sp.start_date) continue;
    if ((op.cne_points ?? null) !== (sp.cne_points ?? null)) continue;

    // ── Stage 2 — evidence only ──────────────────────────────────────────────
    const titleShared = fShared(sp.title_en || sp.title_zh, op.title_en || op.title_zh);
    const providerShared = fShared(sp.provider_full_en || sp.provider_full_zh,
                                   op.provider_full_en || op.provider_full_zh);
    pairs.push({
      code: 'POSSIBLE_DUPLICATE_SUBMISSION',
      // ‼️ `warn` for a stronger pair, `info` otherwise — but BOTH are reported.
      severity: (titleShared.length || providerShared.length) ? 'warn' : 'info',
      pairKey: fPairKey(sid, other.id),
      other: {
        id: other.id, status: other.status || null,
        title: op.title_en || op.title_zh || null,
        provider: op.provider_full_en || op.provider_full_zh || null,
        approved_course_id: other.approved_course_id || null,
      },
      observed: {
        same_submitter: true, same_start_date: true, same_cne_points: true,
        start_date: sp.start_date, cne_points: sp.cne_points ?? null,
      },
      title_shared_tokens: titleShared,
      provider_shared_tokens: providerShared,
      // ‼️ THE THRESHOLD IS >= 1 BECAUSE THAT INVENTS THE LEAST. In the measured
      // corpus no candidate pair sat between 0 and 5 shared tokens, so any cut
      // in 1..5 fits it equally and the range is untested. Taking the minimum
      // that uses the signal at all keeps the unsupported part of the claim as
      // small as possible — and the tokens are printed either way, so a pair
      // resting on one weak token is visible rather than asserted.
      evidence_strength: (titleShared.length || providerShared.length)
        ? 'stronger_overlap' : 'stage1_only',
      // ‼️ NO ACTION. Not reject, not delete, not merge. A human opens both.
      action: null,
    });
  }
  // Stable order: stronger evidence first, then by pair key so the list does not
  // reshuffle between renders.
  return pairs.sort((x, y) =>
    (y.title_shared_tokens.length + y.provider_shared_tokens.length)
    - (x.title_shared_tokens.length + x.provider_shared_tokens.length)
    || x.pairKey.localeCompare(y.pairKey));
}

// ── The batch ────────────────────────────────────────────────────────────────
// Order is render order: identity first, because the other two are only
// interpretable once the reader knows which organisation is being discussed.
function providerIdentityFindings(payload, dict) {
  const match = providerLabelMatch(payload, dict);
  const out = [match];
  const lang = providerLanguageFieldMismatch(payload);
  if (lang) out.push(lang);
  const gap = providerBilingualGap(payload, dict, match);
  if (gap) out.push(gap);
  return out;
}

Object.assign(window, {
  // ‼️ A vm-based test CANNOT catch an omission here: in a vm sandbox
  // `window === sandbox`, so a top-level function declaration is visible on
  // `window` whether or not it is listed. Only build.mjs's required-globals
  // smoke check sees the truth — and it did, by failing this build.
  duplicateReviewFindings,
  conventionFindings,
  knownFacilityInProviderField,
  completenessFindings,
  cnePointsFindings,
  endDateMissing,
  providerNameTrailingNoise,
  providerIdentityFindings,
  providerLabelMatch,
  providerLanguageFieldMismatch,
  providerBilingualGap,
});
